import { HTTP_METHODS } from "@src/constants/http-methods.constant";
import { INTERNAL_API_ENDPOINTS } from "@src/constants/internal-api-endpoints-constants";
import type { DigitalFingerprint } from "@src/services/user-digital-info.service";
import type { ApiCallConfig } from "@src/utils/api-client";

function sanitizeString(value: unknown, maxLength: number): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.replace(/[^\x20-\x7E]/g, "").slice(0, maxLength);
}

function sanitizeDeviceType(value: unknown): string | undefined {
  const sanitized = sanitizeString(value, 16)?.toLowerCase();
  if (!sanitized) return undefined;
  return ["desktop", "mobile", "tablet"].includes(sanitized) ? sanitized : undefined;
}

/**
 * Builds the callApi config for the user-digital-info endpoint.
 * Sanitizes and filters the DigitalFingerprint fields before sending.
 * The userId is included in the JSON request body so it is not exposed in a
 * URL query string.
 */
export function buildUserDigitalInfoConfig(
  userId: string,
  payload: DigitalFingerprint,
): ApiCallConfig {
  const body: Record<string, string> = {};

  body.userId = userId;

  const fields: Array<[string, string | undefined]> = [
    ["browser_name", sanitizeString(payload?.browser_name, 64)],
    ["browser_version", sanitizeString(payload?.browser_version, 32)],
    ["os_name", sanitizeString(payload?.os_name, 64)],
    ["os_version", sanitizeString(payload?.os_version, 32)],
    ["device_type", sanitizeDeviceType(payload?.device_type)],
    ["language", sanitizeString(payload?.language, 16)],
    ["utm_source", sanitizeString(payload?.utm_source, 100)],
    ["utm_medium", sanitizeString(payload?.utm_medium, 100)],
    ["utm_campaign", sanitizeString(payload?.utm_campaign, 100)],
    ["utm_content", sanitizeString(payload?.utm_content, 100)],
    ["utm_theme", sanitizeString(payload?.utm_theme, 100)],
  ];

  for (const [key, value] of fields) {
    if (value !== undefined) {
      body[key] = value;
    }
  }

  return {
    method: HTTP_METHODS.POST,
    endpoint: INTERNAL_API_ENDPOINTS.USER_DIGITAL_INFO,
    body: body as Record<string, unknown>,
  };
}
